import React from 'react';
import { Link } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import '../flaticon.css';
import { getCondominiumHeaderHighlight } from '../pages/CondominiumDetails';

const condoTypeLabels = {
  house: 'CASA',
  apartment: 'APARTAMENTO',
  loft: 'LOFT',
  warehouse: 'GALPÃO',
  store: 'LOJA',
  building: 'PRÉDIO',
  office: 'SALA',
  land: 'TERRENO',
  farm: 'SÍTIO',
  countryHouse: 'CHÁCARA',
  commercial: 'COMERCIAL',
  rural: 'RURAL'
};

function getCondoTypeCode(condo) {
  const typeLabel = condoTypeLabels[condo.type] || 'CONDOMÍNIO';
  return condo.code ? `${typeLabel} - ${condo.code}` : typeLabel;
}

function getPriceOverlay(condo) {
  const highlight = getCondominiumHeaderHighlight(condo);
  if (highlight.suffix) {
    return `${highlight.value}/${highlight.suffix}`;
  }
  return highlight.value;
}

function parseUnits(count) {
  const n = parseInt(String(count ?? '').replace(/\D/g, ''), 10);
  return n > 0 ? n : null;
}

function formatLandArea(value) {
  const n = parseFloat(String(value ?? '').replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n.toLocaleString('pt-BR')}m²`;
}

function CondominiumCard({
  condo,
  propertyCount = 0,
  isFavorite = false,
  onToggleFavorite,
  onShare,
  viewType = 'grid',
  className = '',
  adminActions = null,
  onEdit,
  onDuplicate,
  onDelete
}) {
  const isList = viewType === 'list';
  const cover = condo.coverImage || condo.images?.[0];
  const units = parseUnits(condo.condominiumPropertiesCount);
  const landArea = formatLandArea(condo.landArea ?? condo.area);
  const parking = condo.parkingSpaces != null ? parseInt(condo.parkingSpaces, 10) : null;
  const detailPath = `/condominios/${condo.id}`;

  return (
    <div
      className={`border rounded-lg overflow-hidden shadow-md bg-white transition-all duration-500 transform hover:shadow-[0_0_30px_-5px_rgba(0,0,0,0.2)] ${
        isList ? 'flex flex-row' : 'min-h-[450px] flex flex-col'
      } ${className}`}
    >
      <div
        className={`relative overflow-hidden ${
          isList ? 'w-[400px] shrink-0 self-stretch' : 'h-[220px]'
        }`}
      >
        <Link to={detailPath} className="block w-full h-full" aria-label={`Ver condomínio: ${condo.title || 'Condomínio'}`}>
        <div
          className={`transform transition-transform duration-700 hover:scale-110 ${
            isList ? 'absolute inset-0' : 'relative w-full h-full'
          }`}
        >
          {cover ? (
            <LazyLoadImage
              src={cover}
              alt={condo.title || 'Condomínio'}
              className="w-full h-full object-cover absolute inset-0"
              wrapperClassName="!block w-full h-full bg-gray-100"
              wrapperProps={{ style: { width: '100%', height: '100%', display: 'block' } }}
              effect="blur"
              placeholder={<div className="w-full h-full bg-gray-200 animate-pulse" />}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 text-sm">
              Sem imagem
            </div>
          )}
        </div>
        </Link>

        <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center pointer-events-none">
          <p className="text-white text-base font-lato font-medium bg-gray-900/50 px-2 py-0.5 rounded backdrop-blur-sm">
            {getPriceOverlay(condo)}
          </p>
        </div>

        <div className="absolute top-2 left-2 flex gap-2 pointer-events-none">
          <span className="bg-gray-900/50 text-white px-3 py-1 rounded text-sm backdrop-blur-sm">
            {condo.purpose === 'rent' ? 'Aluguel' : 'Venda'}
          </span>
          {(condo.showInHome || condo.isFeatured || condo.displayTag === 'featured') && (
            <span className="bg-yellow-500/90 text-white px-2 py-1 rounded text-sm backdrop-blur-sm flex items-center gap-1 group relative">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path
                  fillRule="evenodd"
                  d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-8 left-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                Condomínio em destaque
              </div>
            </span>
          )}
        </div>

        <div className="absolute top-2 right-2 flex gap-2">
          {condo.tour360?.type && (
            <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm group relative">
              <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-10 right-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                {condo.tour360.type === 'matterport'
                  ? 'Contém tour 3D/360°'
                  : condo.tour360.type === 'edm360'
                    ? 'Contém tour 360°'
                    : 'Contém tour 360° Marzipano'}
              </div>
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-white">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
                <path d="M12 3C14.5 3 17 7 17 12C17 17 14.5 21 12 21C9.5 21 7 17 7 12C7 7 9.5 3 12 3Z" stroke="currentColor" strokeWidth="1.5" />
                <path d="M3 12H21" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </div>
          )}
          {condo.videoUrl && (
            <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm group relative">
              <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-10 right-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                Contém vídeo
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-white">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
                />
              </svg>
            </div>
          )}
          {condo.floorPlans?.length > 0 && (
            <div className="bg-gray-900/50 px-2 py-1 rounded backdrop-blur-sm group relative">
              <div className="opacity-0 bg-black text-white text-xs rounded py-1 px-2 absolute -bottom-10 right-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                Contém planta baixa
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="2" width="20" height="20" rx="2" />
                <path d="M2 7h20" />
                <path d="M12 7v15" />
              </svg>
            </div>
          )}
        </div>
      </div>

      <div
        className={
          isList
            ? 'flex-1 p-4 flex flex-col justify-between min-w-0'
            : 'p-4 flex flex-col flex-1 justify-between'
        }
      >
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-gray-500 flex items-center gap-2 flex-wrap">
              {getCondoTypeCode(condo)}
              {(condo.isReserved || condo.displayTag === 'reserved') && (
                <span className="bg-red-100 text-red-800 text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase whitespace-nowrap">
                  Reservado
                </span>
              )}
              {condo.displayTag && condo.displayTag !== 'reserved' && condo.displayTag !== 'featured' && (
                <span
                  className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase whitespace-nowrap ${
                    condo.displayTag === 'preLaunch'
                      ? 'bg-blue-100 text-blue-800'
                      : condo.displayTag === 'launch'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {condo.displayTag === 'preLaunch'
                    ? 'Pré-lançamento'
                    : condo.displayTag === 'launch'
                      ? 'Lançamento'
                      : condo.displayTag}
                </span>
              )}
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              {onToggleFavorite && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(condo);
                  }}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors group relative"
                  title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill={isFavorite ? 'currentColor' : 'none'}
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className={`w-4 h-4 transition-colors ${
                      isFavorite ? 'text-red-500' : 'text-gray-400 group-hover:text-red-500'
                    }`}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                    />
                  </svg>
                </button>
              )}
              {onShare && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare(condo);
                  }}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors group relative"
                  title="Compartilhar"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-4 h-4 text-gray-400 group-hover:text-green-500 transition-colors"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.935-2.186 2.25 2.25 0 00-3.935-2.186z"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <h3
            className={`font-montserrat font-semibold text-[#444444] line-clamp-2 transition-colors duration-200 hover:text-[var(--primary-color,#3B82F6)] mb-2 ${
              isList ? 'text-xl' : 'text-[1.1rem]'
            }`}
          >
            <Link to={detailPath}>{condo.title || 'Condomínio'}</Link>
          </h3>
        </div>

        <div>
          <p className="font-lato text-gray-600 mb-4 flex items-center gap-2 text-[13px]">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
            </svg>
            <span className="truncate">
              {condo.address || [condo.neighborhood, condo.city].filter(Boolean).join(', ') || 'Localização não informada'}
            </span>
          </p>

          <div
            className={`${
              isList ? 'flex flex-wrap gap-6' : 'grid grid-cols-2 gap-4'
            } text-sm font-lato text-gray-500`}
          >
            <div className="flex items-center gap-3">
              <i className="flaticon-bed text-lg" />
              <span>{units != null ? `${units} Unidades` : '— Unidades'}</span>
            </div>
            <div className="flex items-center gap-3">
              <i className="flaticon-square text-lg" />
              <span>{landArea ? `${landArea} terreno` : '— área'}</span>
            </div>
            <div className="flex items-center gap-3">
              <i className="flaticon-bathtub text-lg" />
              <span>
                {propertyCount} Imóvel{propertyCount === 1 ? '' : 'is'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="flaticon-car text-lg" />
              <span>{parking != null && parking > 0 ? `${parking} Vagas` : '— Vagas'}</span>
            </div>
          </div>

          <div className="border-t border-gray-200 my-4" />

          <div className={isList ? 'flex gap-2 w-full' : ''}>
          <Link
            to={detailPath}
            className={`${isList ? 'flex-1 w-full' : 'w-full'} rounded-lg py-2 transition-all duration-300 text-center no-underline inline-block`}
            style={{
              backgroundColor: 'var(--btn-outline-bg, #ffffff)',
              color: 'var(--btn-outline-text, #3B82F6)',
              border: '1px solid var(--btn-outline-border, #3B82F6)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
              e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
              e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
            }}
          >
            Ver detalhes
          </Link>
          </div>
        </div>

        {adminActions &&
          (adminActions.canEdit || adminActions.canDelete || adminActions.canDuplicate) && (
            <div className="flex justify-between mt-2 border-t border-gray-100 pt-2">
                {adminActions.canEdit && onEdit && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(condo);
                      }}
                      className="p-2 text-blue-500 hover:text-blue-600 flex-1 flex justify-center"
                      title="Editar"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                        />
                      </svg>
                    </button>
                    {(adminActions.canDuplicate || adminActions.canDelete) && (
                      <div className="w-px bg-gray-100" />
                    )}
                  </>
                )}
                {adminActions.canDuplicate && onDuplicate && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(condo);
                      }}
                      className="p-2 text-green-500 hover:text-green-600 flex-1 flex justify-center"
                      title="Duplicar"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
                        />
                      </svg>
                    </button>
                    {adminActions.canDelete && <div className="w-px bg-gray-100" />}
                  </>
                )}
                {adminActions.canDelete && onDelete && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(condo);
                    }}
                    className="p-2 text-red-500 hover:text-red-600 flex-1 flex justify-center"
                    title="Remover"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                      />
                    </svg>
                  </button>
                )}
            </div>
          )}
      </div>
    </div>
  );
}

export default CondominiumCard;
